import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Terminal, FileText, Receipt, Users, LogOut, Package, X,
  ArrowDownToLine, ArrowUpFromLine, Settings, BarChart3, ShoppingCart,
  ShieldCheck, IndianRupee, Truck, Banknote, ClipboardList, ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/command", icon: Terminal, label: "Command Center" },
  { path: "/invoices", icon: FileText, label: "Invoices" },
  {
    path: "/receivables",
    icon: ArrowDownToLine,
    label: "Receivables",
    children: [
      { path: "/receipts", icon: IndianRupee, label: "Receipts" },
    ],
  },
  {
    path: "/payables",
    icon: ArrowUpFromLine,
    label: "Payables",
    children: [
      { path: "/payments", icon: Banknote, label: "Payments" },
    ],
  },
  { path: "/reports", icon: BarChart3, label: "Reports" },
  { path: "/items", icon: Package, label: "Items & Services" },
  {
    path: "/purchases",
    icon: ShoppingCart,
    label: "Purchases",
    children: [
      { path: "/purchase-orders", icon: ClipboardList, label: "Purchase Orders" },
    ],
  },
  { path: "/expenses", icon: Receipt, label: "Expenses" },
  { path: "/clients", icon: Users, label: "Clients" },
  { path: "/vendors", icon: Truck, label: "Vendors" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

// Persist expansion state across navigations so opening a group "sticks" until
// the user explicitly collapses it.
const STORAGE_KEY = "bizflowa.sidebar.expanded";

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const Sidebar = ({ onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();

  // Group keyed by parent path → boolean (expanded?). Initial state:
  //   1) Honour the user's stored preference.
  //   2) Otherwise auto-expand any group whose current route is active.
  const initialExpanded = useMemo(() => {
    const stored = readStored() || {};
    const result = {};
    for (const item of navItems) {
      if (!item.children?.length) continue;
      const anyChildActive = item.children.some(
        (c) => location.pathname === c.path || location.pathname.startsWith(c.path)
      );
      const parentActive = location.pathname === item.path
        || (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
      const hasStoredPref = Object.prototype.hasOwnProperty.call(stored, item.path);
      result[item.path] = hasStoredPref ? !!stored[item.path] : (anyChildActive || parentActive);
    }
    return result;
  }, []);

  const [expanded, setExpanded] = useState(initialExpanded);

  // Keep groups containing the active route open even if the user manually
  // collapsed something else — but never force-close.
  useEffect(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const item of navItems) {
        if (!item.children?.length) continue;
        const anyChildActive = item.children.some(
          (c) => location.pathname === c.path || location.pathname.startsWith(c.path)
        );
        if (anyChildActive && !next[item.path]) {
          next[item.path] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [location.pathname]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(expanded)); } catch { /* ignore */ }
  }, [expanded]);

  const toggleExpand = (path, e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const handleNav = () => {
    if (onClose) onClose();
  };

  return (
    <aside className="w-64 h-full bg-white border-r border-[#e2e8f0] flex flex-col shrink-0" data-testid="sidebar">
      <div className="p-6 border-b border-[#e2e8f0] flex items-center justify-between">
        <Link to="/dashboard" onClick={handleNav} className="flex items-center gap-2.5">
          <img src="/bizflowa-logo.png" alt="BizFlowa" className="h-8 w-8 rounded-lg object-contain" />
          <span className="font-outfit text-lg font-bold tracking-tight text-[#0f172a]">BizFlowa</span>
        </Link>
        {onClose && (
          <button onClick={onClose} className="lg:hidden p-1 rounded-md text-[#64748b] hover:bg-[#f1f5f9]" data-testid="sidebar-close-btn">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
          const hasChildren = (item.children || []).length > 0;
          const isExpanded = hasChildren && !!expanded[item.path];
          const groupKey = item.label.toLowerCase().replace(/\s+/g, "-");
          return (
            <div key={item.path}>
              <div
                className={cn(
                  "flex items-center rounded-lg overflow-hidden",
                  isActive
                    ? "bg-[#4338ca]/10 text-[#4338ca]"
                    : "text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a]"
                )}
              >
                <Link
                  to={item.path}
                  onClick={handleNav}
                  data-testid={`nav-${groupKey}`}
                  className="flex flex-1 items-center gap-3 px-3 py-2.5 text-sm font-medium min-w-0"
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
                {hasChildren && (
                  <button
                    type="button"
                    onClick={(e) => toggleExpand(item.path, e)}
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? "Collapse" : "Expand"} ${item.label}`}
                    data-testid={`nav-toggle-${groupKey}`}
                    className="px-2 py-2.5 hover:bg-black/5 rounded-r-lg transition-colors"
                  >
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 transition-transform duration-200",
                        isExpanded ? "rotate-180" : "rotate-0"
                      )}
                    />
                  </button>
                )}
              </div>
              {hasChildren && isExpanded && (
                <div
                  className="ml-3 mt-1 mb-1 pl-3 border-l border-[#e2e8f0] space-y-0.5"
                  data-testid={`nav-children-${groupKey}`}
                >
                  {item.children.map((child) => {
                    const childActive = location.pathname === child.path || location.pathname.startsWith(child.path);
                    return (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={handleNav}
                        data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={cn(
                          "flex items-center gap-2.5 px-3 py-2 rounded-md text-xs font-medium",
                          childActive
                            ? "bg-[#4338ca]/10 text-[#4338ca]"
                            : "text-[#94a3b8] hover:text-[#0f172a] hover:bg-[#f8fafc]"
                        )}
                      >
                        <child.icon className="h-3.5 w-3.5" />
                        {child.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {/* Admin Portal - only for admin users */}
        {(user?.is_admin || user?.is_super_admin) && (
          <Link
            to="/akupara-admin"
            onClick={handleNav}
            data-testid="nav-admin-portal"
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium mt-2 border-t border-[#f1f5f9] pt-3",
              location.pathname === "/akupara-admin"
                ? "bg-[#7c3aed]/10 text-[#7c3aed]"
                : "text-[#7c3aed]/70 hover:bg-[#7c3aed]/5 hover:text-[#7c3aed]"
            )}
          >
            <ShieldCheck className="h-4 w-4" />
            Admin Portal
          </Link>
        )}
      </nav>

      <div className="p-4 border-t border-[#e2e8f0]">
        <div className="flex items-center gap-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-[#4338ca]/10 flex items-center justify-center text-[#4338ca] text-sm font-semibold">
              {user?.name?.[0]}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#0f172a] truncate">{user?.name}</p>
            <p className="text-xs text-[#64748b] truncate">{user?.business_name || user?.email}</p>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="p-1.5 rounded-md text-[#64748b] hover:text-[#ef4444] hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
};
